import { div, h1, h2, p, span, button, table, thead, tbody, tr, th, td, textarea, select, option, input, fnstate } from '@srfnstack/fntags'

// Governance & Safety dashboard: the emergency kill switch + the live feed of
// everything hitting limits or getting blocked.
export const Governance = () => {
  const controls = fnstate({ global_paused: false, paused_job_types: [], paused_tenants: [], reason: null })
  const events = fnstate([])
  const summary = fnstate([])
  const loading = fnstate(true)
  const busy = fnstate(false)
  const savingLimits = fnstate(false)
  const confirmOpen = fnstate(false)
  const rateLimitsText = fnstate('')
  const anomalyText = fnstate('')
  const flags = fnstate([])

  const load = async () => {
    loading(true)
    try {
      const r = await fetch('/api/system/controls')
      if (r.ok) {
        const c = await r.json()
        controls(c)
        rateLimitsText(c.rate_limits ? JSON.stringify(c.rate_limits, null, 2) : '')
        anomalyText(c.anomaly_thresholds ? JSON.stringify(c.anomaly_thresholds, null, 2) : '')
      }
      const e = await fetch('/api/system/governance-events?limit=100')
      if (e.ok) {
        const data = await e.json()
        events(Array.isArray(data.events) ? data.events : [])
        summary(Array.isArray(data.summary) ? data.summary : [])
      }
      const ff = await fetch('/api/system/feature-flags')
      if (ff.ok) flags(await ff.json())
    } catch { /* leave defaults */ }
    loading(false)
  }

  const saveFlag = async (flag, value) => {
    try {
      const res = await fetch('/api/system/feature-flags', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ flag_key: flag.flag_key, tenant_id: flag.tenant_id, value })
      })
      if (res.ok) await window.alert(`Saved ${flag.flag_key}.`)
      else { const e = await res.json().catch(() => ({})); await window.alert(`Failed: ${e.error || 'error'}`) }
    } catch (err) { await window.alert(`Failed: ${err.message}`) }
  }

  const setPaused = async (paused) => {
    busy(true)
    confirmOpen(false)
    try {
      const r = await fetch('/api/system/controls', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ global_paused: paused, reason: paused ? 'Emergency halt from dashboard' : 'Resumed from dashboard' })
      })
      if (r.ok) {
        const d = await r.json()
        if (d.controls) controls(d.controls)
      } else {
        const e = await r.json().catch(() => ({}))
        await window.alert(`Failed: ${e.error || 'error'}`)
      }
    } catch (err) {
      await window.alert(`Failed: ${err.message}`)
    }
    busy(false)
  }

  const saveLimits = async () => {
    let rate, anomaly
    try {
      rate = rateLimitsText().trim() ? JSON.parse(rateLimitsText()) : {}
      anomaly = anomalyText().trim() ? JSON.parse(anomalyText()) : {}
    } catch (err) {
      await window.alert(`Invalid JSON: ${err.message}`)
      return
    }
    savingLimits(true)
    try {
      const r = await fetch('/api/system/controls', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rate_limits: rate, anomaly_thresholds: anomaly })
      })
      if (r.ok) await window.alert('Limits saved — the engine applies them within ~60s.')
      else { const e = await r.json().catch(() => ({})); await window.alert(`Failed: ${e.error || 'error'}`) }
    } catch (err) { await window.alert(`Failed: ${err.message}`) }
    savingLimits(false)
  }

  const eventBadgeClass = (t) => {
    if (['HALT', 'CIRCUIT_OPEN', 'CHAIN_DEPTH_ABORT', 'LIFETIME_CAP', 'ANOMALY_PAUSE', 'TOKEN_BUDGET_EXCEEDED'].includes(t)) return 'badge-error'
    if (['RATE_LIMITED', 'DEDUP_SKIP'].includes(t)) return 'badge-warning'
    return 'badge-ghost'
  }

  load()

  return div({ class: 'p-6' },
    h1({ class: 'text-3xl font-bold mb-1' }, 'Governance & Safety'),
    p({ class: 'text-sm opacity-70 mb-6' }, 'Emergency controls for the background job engine.'),
    loading.bindAs(l => l
      ? div({ class: 'flex justify-center p-12' }, span({ class: 'loading loading-spinner loading-lg' }))
      : controls.bindAs(c => div({ class: 'card bg-base-200 border border-base-300 p-6 max-w-xl' },
        h2({ class: 'text-xl font-bold mb-3' }, 'Job Engine'),
        div({ class: 'mb-4 flex items-center gap-3' },
          span({ class: 'text-sm opacity-70' }, 'Status:'),
          c.global_paused
            ? span({ class: 'badge badge-error badge-lg' }, '🛑 HALTED')
            : span({ class: 'badge badge-success badge-lg' }, '▶ Running')
        ),
        (c.paused_job_types && c.paused_job_types.length > 0)
          ? div({ class: 'mb-4' },
            span({ class: 'text-sm opacity-70 mr-2' }, 'Auto-paused job types:'),
            ...c.paused_job_types.map(t => span({ class: 'badge badge-warning badge-sm mr-1' }, t)))
          : '',
        c.global_paused
          ? div({},
            c.reason ? p({ class: 'text-sm opacity-70 mb-3' }, `Reason: ${c.reason}`) : '',
            button({ class: 'btn btn-success', disabled: busy.bindAttr(b => b), onclick: () => setPaused(false) }, 'Resume Processing'))
          : button({ class: 'btn btn-error', disabled: busy.bindAttr(b => b), onclick: () => confirmOpen(true) }, '🛑 Emergency Halt'),
        p({ class: 'text-xs opacity-50 mt-4' }, 'Halting stops ALL background jobs for every tenant. In-flight jobs finish; no new jobs are claimed until resumed.')
      ))
    ),
    div({ class: 'mt-8' },
      h2({ class: 'text-xl font-bold mb-3' }, 'Recent Governance Events'),
      summary.bindAs(s => (!Array.isArray(s) || s.length === 0)
        ? ''
        : div({ class: 'flex flex-wrap gap-2 mb-4' }, s.map(row => span({ class: 'badge badge-outline' }, `${row.event_type}: ${row.count} (24h)`)))),
      events.bindAs(list => (!Array.isArray(list) || list.length === 0)
        ? p({ class: 'text-sm opacity-60' }, 'No governance events recorded. 🎉')
        : div({ class: 'overflow-x-auto' },
          table({ class: 'table table-sm' },
            thead(tr(th('Time'), th('Event'), th('Service'), th('Tenant'), th('Detail'))),
            tbody(...list.map(ev => tr(
              td({ class: 'whitespace-nowrap text-xs' }, new Date(ev.created_at).toLocaleString()),
              td(span({ class: `badge badge-sm ${eventBadgeClass(ev.event_type)}` }, ev.event_type)),
              td({ class: 'text-xs' }, ev.service || '—'),
              td({ class: 'text-xs' }, ev.tenant_id == null ? '—' : String(ev.tenant_id)),
              td({ class: 'text-xs opacity-70' }, ev.detail ? JSON.stringify(ev.detail) : '')
            )))
          )))
    ),
    div({ class: 'card bg-base-200 border border-base-300 p-6 mt-8 max-w-2xl' },
      h2({ class: 'text-xl font-bold mb-1' }, 'Tunable Limits'),
      p({ class: 'text-sm opacity-70 mb-4' }, 'Override rate limits + anomaly ceilings without a redeploy (applied within ~60s). Leave blank to use code defaults — raise before a big bulk operation.'),
      div({ class: 'form-control mb-3' },
        span({ class: 'label-text text-sm mb-1' }, 'Rate limits (JSON)'),
        textarea({
          class: 'textarea textarea-bordered font-mono text-xs h-28',
          placeholder: '{\n  "printify": {"per_min_per_tenant": 150, "per_min_global": 400},\n  "gemini": {"per_min_per_tenant": 60, "per_min_global": 150}\n}',
          value: rateLimitsText.bindAttr(t => t),
          oninput: (e) => rateLimitsText(e.target.value)
        })
      ),
      div({ class: 'form-control mb-3' },
        span({ class: 'label-text text-sm mb-1' }, 'Anomaly ceilings — jobs per 5 min (JSON; "_default" for the fallback)'),
        textarea({
          class: 'textarea textarea-bordered font-mono text-xs h-28',
          placeholder: '{\n  "PRINTIFY_PUSH": 120,\n  "SEED_DISCOVER": 60,\n  "_default": 500\n}',
          value: anomalyText.bindAttr(t => t),
          oninput: (e) => anomalyText(e.target.value)
        })
      ),
      div({ class: 'card-actions justify-end' },
        button({ class: 'btn btn-primary', disabled: savingLimits.bindAttr(s => s), onclick: saveLimits },
          savingLimits.bindAs(s => s ? span({ class: 'loading loading-spinner loading-sm' }) : 'Save Limits'))
      )
    ),
    div({ class: 'card bg-base-200 border border-base-300 p-6 mt-8 max-w-2xl' },
      h2({ class: 'text-xl font-bold mb-1' }, 'Feature Flags'),
      p({ class: 'text-sm opacity-70 mb-4' }, 'Global (or per-tenant) toggles. publish_mode switches between the legacy Printify internal-finalize path and the new Etsy-direct path.'),
      flags.bindAs(list => (!Array.isArray(list) || list.length === 0)
        ? p({ class: 'text-sm opacity-60' }, 'No flags defined.')
        : div({ class: 'space-y-3' }, list.map(flag => {
          const scope = flag.tenant_id == null ? 'global' : `tenant ${flag.tenant_id}`
          if (flag.flag_key === 'publish_mode') {
            return div({ class: 'flex items-center gap-3 flex-wrap' },
              span({ class: 'font-mono text-sm w-40' }, `${flag.flag_key} (${scope})`),
              select({ class: 'select select-bordered select-sm', onchange: (e) => saveFlag(flag, e.target.value) },
                option({ value: 'printify_internal', selected: flag.value === 'printify_internal' }, 'printify_internal (legacy)'),
                option({ value: 'etsy_direct', selected: flag.value === 'etsy_direct' }, 'etsy_direct (new)')))
          }
          const valInput = input({ class: 'input input-bordered input-sm font-mono flex-1', value: JSON.stringify(flag.value) })
          return div({ class: 'flex items-center gap-3 flex-wrap' },
            span({ class: 'font-mono text-sm w-40' }, `${flag.flag_key} (${scope})`),
            valInput,
            button({
              class: 'btn btn-xs btn-primary',
              onclick: () => { try { saveFlag(flag, JSON.parse(valInput.value)) } catch (e) { window.alert(`Invalid JSON: ${e.message}`) } }
            }, 'Save'))
        })))
    ),
    confirmOpen.bindAs(open => open
      ? div({ class: 'modal modal-open' },
        div({ class: 'modal-box' },
          h2({ class: 'font-bold text-lg' }, 'Halt the entire job engine?'),
          p({ class: 'py-4' }, 'This immediately stops ALL background job processing for every tenant until you resume it. Use this when something is going wrong (a runaway loop, external-API spam, etc.).'),
          div({ class: 'modal-action' },
            button({ class: 'btn btn-ghost', onclick: () => confirmOpen(false) }, 'Cancel'),
            button({ class: 'btn btn-error', onclick: () => setPaused(true) }, 'Yes, halt everything')
          )
        )
      )
      : '')
  )
}
